// Imagina Ser: página del cuento. Paso 1, la familia aprueba a sus personajes; paso 2, el libro abierto
// (una doble página a la vez, con el texto en la hoja derecha), edición, cambios de ilustración, pago y PDF.
(() => {
  const ESPERA_MS = 4000;
  const OCUPADO = new Set(["en_cola", "creando_personajes", "escribiendo", "ilustrando", "completando"]);
  const ANTES_DEL_LIBRO = new Set(["en_cola", "creando_personajes", "revisar_personajes"]);
  const JSPDF = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";

  function iniciar(raiz) {
    if (raiz.dataset.listo) return;
    raiz.dataset.listo = "1";
    const $ = (sel) => raiz.querySelector(`[data-is="${sel}"]`);
    const api = raiz.dataset.api.replace(/\/$/, "");
    const id = new URLSearchParams(location.search).get("id");
    let cuento = null;
    let vistas = [];
    let actual = 0;
    let modo = null; // null | "texto" | "dibujo"
    let claveHoja = "";
    let temporizador = null;

    $("ir-crear").href = raiz.dataset.crear || "/";
    if (!id) return mostrarSinCuento();

    // --- Datos ---

    async function consultar() {
      clearTimeout(temporizador);
      try {
        const r = await fetch(`${api}/api/cuentos/${encodeURIComponent(id)}`, { cache: "no-store" });
        if (r.status === 404) return mostrarSinCuento();
        if (!r.ok) throw new Error();
        cuento = await r.json();
        pintar();
        mostrarError("");
      } catch {
        mostrarError("Se cortó la conexión. Seguimos intentando…");
      }
      if (!cuento || ocupado()) temporizador = setTimeout(consultar, ESPERA_MS);
    }

    const visibles = () => cuento.paginas.filter((p) => !p.bloqueada);
    const ocupado = () => OCUPADO.has(cuento.estado)
      || cuento.personajes.some((p) => p.generando)
      || visibles().some((p) => p.estado === "en_cola" || p.estado === "generando");

    async function llamar(ruta, metodo, cuerpo) {
      const r = await fetch(`${api}/api/cuentos/${encodeURIComponent(id)}${ruta}`, {
        method: metodo,
        headers: { "content-type": "application/json" },
        body: JSON.stringify(cuerpo || {})
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "No pudimos guardar el cambio.");
      return d;
    }

    // Portada, páginas visibles y tramos bloqueados, en orden de lectura.
    function construirVistas() {
      const lista = [];
      for (const p of [...cuento.paginas].sort((a, b) => a.n - b.n)) {
        if (p.n === 0) lista.push({ tipo: "portada", n: 0 });
        else if (!p.bloqueada) lista.push({ tipo: "pagina", n: p.n });
        else if (lista.at(-1)?.tipo === "bloque") lista.at(-1).hasta = p.n;
        else lista.push({ tipo: "bloque", desde: p.n, hasta: p.n });
      }
      return lista;
    }

    // --- Pintado general ---

    function mostrarSinCuento() {
      $("cargando").classList.add("is-oculto");
      $("cuento").classList.add("is-oculto");
      $("sin-cuento").classList.remove("is-oculto");
    }

    function mostrarError(texto) {
      $("error").textContent = texto;
      $("error").classList.toggle("is-oculto", !texto);
    }

    function pintar() {
      $("cargando").classList.add("is-oculto");
      $("cuento").classList.remove("is-oculto");
      $("titulo").textContent = cuento.titulo || "";
      $("titulo").classList.toggle("is-oculto", !cuento.titulo);
      if (cuento.titulo) document.title = `${cuento.titulo} · Imagina Ser`;
      pintarEstado();
      const enPersonajes = ANTES_DEL_LIBRO.has(cuento.estado);
      $("aprobar").classList.toggle("is-oculto", !enPersonajes);
      $("libro").classList.toggle("is-oculto", enPersonajes || !cuento.paginas.length);
      if (enPersonajes) return pintarFichas();
      if (!cuento.paginas.length) return;
      const antes = vistas.length;
      vistas = construirVistas();
      if (!antes || actual >= vistas.length) actual = 0;
      pintarLibro();
    }

    function pintarEstado() {
      const caja = $("estado");
      if (cuento.estado === "error") {
        caja.classList.add("is-oculto");
        return mostrarError(cuento.error || "No pudimos crear este cuento. Escríbenos y lo revisamos.");
      }
      const lista = visibles();
      const hechas = lista.filter((p) => p.estado === "lista").length;
      const textos = {
        en_cola: ["Preparando tu cuento…", 5],
        creando_personajes: ["Dibujando a tus personajes…", 15],
        escribiendo: ["Escribiendo tu historia…", 30]
      };
      let [texto, avance] = textos[cuento.estado] || ["", 0];
      if (!texto && ocupado() && !ANTES_DEL_LIBRO.has(cuento.estado)) {
        texto = `Ilustrando tu cuento: ${hechas} de ${lista.length} láminas listas`;
        avance = 40 + Math.round((60 * hechas) / Math.max(1, lista.length));
      }
      caja.classList.toggle("is-oculto", !texto);
      $("estado-texto").textContent = texto;
      $("barra").style.width = `${avance}%`;
    }

    // --- Paso 1: personajes ---

    function pintarFichas() {
      const listos = cuento.estado === "revisar_personajes";
      const cont = $("fichas");
      const abiertas = new Set([...cont.querySelectorAll("[data-abierta]")].map((el) => el.dataset.i));
      cont.innerHTML = "";
      cuento.personajes.forEach((p, i) => {
        const tarjeta = document.createElement("article");
        tarjeta.className = "is-ficha";
        tarjeta.innerHTML = `
          <div class="is-ficha-img">${p.ficha && !p.generando ? '<img alt="">' : '<div class="is-giro"></div>'}</div>
          <h3></h3>
          <button type="button" class="is-boton-sec is-chico" data-f="cambio">🎨 Pedir un cambio</button>
          <div class="is-ficha-cambio is-oculto">
            <textarea class="is-campo" rows="2" maxlength="300" placeholder="Ej.: el pelo más ondulado y con chasquilla"></textarea>
            <button type="button" class="is-boton is-chico" data-f="dibujar">Dibujar de nuevo</button>
          </div>`;
        tarjeta.querySelector("h3").textContent = p.nombre;
        const img = tarjeta.querySelector("img");
        if (img) { img.src = p.ficha; img.alt = `Personaje ilustrado: ${p.nombre}`; }
        const boton = tarjeta.querySelector('[data-f="cambio"]');
        const caja = tarjeta.querySelector(".is-ficha-cambio");
        const sinCambios = cuento.regen_personajes.usadas >= cuento.regen_personajes.max;
        boton.disabled = !listos || p.generando || sinCambios || cuento.personajes.some((x) => x.generando);
        const abrir = () => { caja.classList.remove("is-oculto"); tarjeta.dataset.abierta = "1"; tarjeta.dataset.i = i; };
        if (abiertas.has(String(i)) && !boton.disabled) abrir();
        boton.addEventListener("click", () => { abrir(); caja.querySelector("textarea").focus(); });
        tarjeta.querySelector('[data-f="dibujar"]').addEventListener("click", async (ev) => {
          ev.target.disabled = true;
          try {
            await llamar(`/personajes/${i}/regenerar`, "POST", { indicacion: caja.querySelector("textarea").value });
            await consultar();
          } catch (e) {
            mostrarError(e.message);
            ev.target.disabled = false;
          }
        });
        cont.appendChild(tarjeta);
      });
      const { usadas, max } = cuento.regen_personajes;
      $("nota-cambios").textContent = listos ? `Cambios de personajes usados: ${usadas} de ${max}. Al aprobar, borramos las fotos originales.` : "";
      const btn = $("aprobar-btn");
      btn.disabled = !listos || cuento.personajes.some((p) => p.generando);
      btn.textContent = listos ? "✨ ¡Me encantan! Crear mi cuento" : "Dibujando a tus personajes…";
    }

    $("aprobar-btn").addEventListener("click", async (ev) => {
      ev.target.disabled = true;
      try {
        await llamar("/aprobar", "POST");
        await consultar();
      } catch (e) {
        mostrarError(e.message);
        ev.target.disabled = false;
      }
    });

    // --- Paso 2: el libro ---

    const pagina = (n) => cuento.paginas.find((p) => p.n === n);
    // Cada lámina es una doble página del libro impreso: la lámina n son las páginas 2n-1 y 2n.
    const hojas = (desde, hasta = desde) => `${2 * desde - 1}–${2 * hasta}`;

    function pintarLibro() {
      const v = vistas[actual];
      const total = cuento.total_paginas * 2;
      $("indicador").textContent = v.tipo === "portada" ? "Portada" : `Páginas ${v.tipo === "pagina" ? hojas(v.n) : hojas(v.desde, v.hasta)} de ${total}`;
      $("anterior").disabled = actual === 0;
      $("siguiente").disabled = actual === vistas.length - 1;
      const p = v.tipo === "bloque" ? null : pagina(v.n);
      const editable = p && p.estado === "lista";
      $("editar").classList.toggle("is-oculto", !editable);
      $("editar").textContent = v.tipo === "portada" ? "✏️ Editar título" : "✏️ Editar o mover texto";
      $("redibujar").classList.toggle("is-oculto", !(p && ["lista", "error"].includes(p.estado)));
      $("pdf").classList.toggle("is-oculto", cuento.estado !== "completo");
      $("nota-listo").classList.toggle("is-oculto", cuento.estado !== "completo");
      pintarChips();
      if (!modo) pintarHoja(v, p);
    }

    function pintarChips() {
      const nav = $("chips");
      nav.innerHTML = "";
      vistas.forEach((v, i) => {
        const b = document.createElement("button");
        b.type = "button";
        b.className = `is-chip-pag${v.tipo === "bloque" ? " is-chip-bloqueado" : ""}`;
        b.textContent = v.tipo === "portada" ? "📖" : v.tipo === "pagina" ? hojas(v.n) : `🔒 ${hojas(v.desde, v.hasta)}`;
        b.setAttribute("aria-label", v.tipo === "portada" ? "Portada" : v.tipo === "pagina" ? `Páginas ${hojas(v.n)}` : `Páginas ${hojas(v.desde, v.hasta)}, bloqueadas`);
        if (i === actual) b.setAttribute("aria-current", "page");
        b.addEventListener("click", () => ir(i));
        nav.appendChild(b);
      });
    }

    function pintarHoja(v, p) {
      // Solo se redibuja si cambió lo que se ve, así las imágenes no parpadean en cada consulta.
      const clave = JSON.stringify([v, p?.estado, p?.imagen, p?.texto, p?.caja, cuento.titulo, cuento.pagado]);
      if (clave === claveHoja) return;
      claveHoja = clave;
      const hoja = $("hoja");
      hoja.innerHTML = "";
      if (v.tipo === "bloque") return pintarBloque(hoja, v);
      const contenedor = document.createElement("div");
      contenedor.className = v.tipo === "portada" ? "is-hoja is-hoja-portada" : "is-hoja";
      if (v.tipo === "portada") {
        contenedor.innerHTML = '<div class="is-tapa"><img alt=""><h2 class="is-tapa-titulo" data-editable></h2></div>';
        contenedor.querySelector("h2").textContent = cuento.titulo || "";
      } else {
        contenedor.innerHTML = '<img alt=""><span class="is-pliegue" aria-hidden="true"></span><div class="is-texto-hoja"><div data-editable></div></div>';
        contenedor.querySelector("[data-editable]").textContent = p.texto || "";
        ubicarCaja(contenedor.querySelector(".is-texto-hoja"), p);
      }
      const img = contenedor.querySelector("img");
      if (p.imagen) {
        img.src = p.imagen;
        img.alt = v.tipo === "portada" ? `Portada de ${cuento.titulo || "tu cuento"}` : `Ilustración de las páginas ${hojas(v.n)}`;
      } else img.remove();
      if (p.estado !== "lista") {
        const espera = document.createElement("div");
        espera.className = "is-lamina-espera";
        espera.innerHTML = p.estado === "error" ? "😕 No pudimos dibujar esta página. Pide otra ilustración." : '<div class="is-giro"></div>Dibujando esta página…';
        contenedor.appendChild(espera);
      }
      hoja.appendChild(contenedor);
    }

    // Sin ajuste guardado, el cuadro va centrado en el lado que la ilustración dejó despejado.
    function ubicarCaja(el, p) {
      el.classList.toggle("is-texto-izq", p.lado === "izquierda");
      if (!p.caja) return;
      el.classList.add("is-texto-libre");
      Object.assign(el.style, { left: `${p.caja.x}%`, top: `${p.caja.y}%`, width: `${p.caja.w}%` });
      el.style.setProperty("--is-escala", p.caja.f);
    }

    // Lee la caja tal como quedó en pantalla, en % de la lámina, para guardarla.
    function medirCaja(el) {
      const hoja = el.parentElement.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      return {
        x: ((r.left - hoja.left) / hoja.width) * 100,
        y: ((r.top - hoja.top) / hoja.height) * 100,
        w: (r.width / hoja.width) * 100,
        f: Number(el.style.getPropertyValue("--is-escala")) || 1
      };
    }

    // Mover (arrastrando la barra de arriba) y cambiar el ancho (esquina), con mouse o dedo.
    function hacerMovible(el) {
      const tope = (v, min, max) => Math.min(max, Math.max(min, v));
      const fijar = () => {
        const c = medirCaja(el);
        el.classList.add("is-texto-libre");
        Object.assign(el.style, { left: `${c.x}%`, top: `${c.y}%`, width: `${c.w}%` });
        return c;
      };
      const arrastre = (asa, mover) => asa.addEventListener("pointerdown", (ev) => {
        ev.preventDefault();
        asa.setPointerCapture(ev.pointerId);
        const inicio = fijar();
        const hoja = el.parentElement.getBoundingClientRect();
        const [x0, y0] = [ev.clientX, ev.clientY];
        const alMover = (e) => mover(inicio, ((e.clientX - x0) / hoja.width) * 100, ((e.clientY - y0) / hoja.height) * 100);
        const soltar = () => { asa.removeEventListener("pointermove", alMover); asa.removeEventListener("pointerup", soltar); };
        asa.addEventListener("pointermove", alMover);
        asa.addEventListener("pointerup", soltar);
      });
      const mango = document.createElement("div");
      mango.className = "is-caja-mango";
      mango.textContent = "✥ Arrastra para mover";
      const esquina = document.createElement("div");
      esquina.className = "is-caja-esquina";
      esquina.setAttribute("aria-hidden", "true");
      el.prepend(mango);
      el.append(esquina);
      arrastre(mango, (c, dx, dy) => {
        el.style.left = `${tope(c.x + dx, 0, 100 - c.w)}%`;
        el.style.top = `${tope(c.y + dy, 0, 100 - (el.offsetHeight / el.parentElement.offsetHeight) * 100)}%`;
      });
      arrastre(esquina, (c, dx) => { el.style.width = `${tope(c.w + dx, 20, 100 - c.x)}%`; });
    }

    function pintarBloque(hoja, v) {
      const contenedor = document.createElement("div");
      contenedor.className = "is-hoja is-hoja-bloqueada";
      // De fondo, difuminada, la última lámina que la familia sí pudo ver.
      const previa = [...vistas.slice(0, actual)].reverse().map((x) => x.tipo !== "bloque" && pagina(x.n)).find((x) => x?.imagen);
      if (previa) {
        const fondo = document.createElement("img");
        fondo.src = previa.imagen;
        fondo.alt = "";
        fondo.className = "is-fondo-difuso";
        contenedor.appendChild(fondo);
      }
      contenedor.appendChild(raiz.querySelector('template[data-is="oferta"]').content.cloneNode(true));
      const cuantas = v.hasta - v.desde + 1;
      contenedor.querySelector('[data-o="cuantas"]').textContent = `Con el libro completo se desbloquean estas ${cuantas * 2} páginas y todas las demás.`;
      const siguienteGratis = vistas.slice(actual + 1).find((x) => x.tipo === "pagina");
      const gratis = contenedor.querySelector('[data-o="gratis"]');
      const seguir = contenedor.querySelector('[data-o="seguir"]');
      if (siguienteGratis) {
        gratis.textContent = `Las páginas ${hojas(siguienteGratis.n)} también puedes leerlas gratis.`;
        seguir.textContent = `Seguir leyendo → páginas ${hojas(siguienteGratis.n)}`;
        seguir.addEventListener("click", () => ir(vistas.indexOf(siguienteGratis)));
      } else {
        gratis.remove();
        seguir.remove();
      }
      contenedor.querySelector('[data-o="comprar"]')?.addEventListener("click", comprar);
      hoja.appendChild(contenedor);
    }

    function ir(i) {
      if (i < 0 || i >= vistas.length || modo) return;
      actual = i;
      claveHoja = "";
      pintarLibro();
    }

    $("anterior").addEventListener("click", () => ir(actual - 1));
    $("siguiente").addEventListener("click", () => ir(actual + 1));
    raiz.addEventListener("keydown", (ev) => {
      if (modo || ev.target.closest("[contenteditable='true'], textarea, input")) return;
      if (ev.key === "ArrowLeft") ir(actual - 1);
      if (ev.key === "ArrowRight") ir(actual + 1);
    });

    // --- Edición del texto directo en la hoja, y cambios de ilustración ---

    function cerrarPanel() {
      modo = null;
      $("panel").classList.add("is-oculto");
      $("panel").innerHTML = "";
      const el = $("hoja").querySelector("[data-editable]");
      if (el) el.contentEditable = "false";
      claveHoja = "";
      pintarLibro();
    }

    $("editar").addEventListener("click", () => {
      if (modo) return cerrarPanel();
      const v = vistas[actual];
      const el = $("hoja").querySelector("[data-editable]");
      if (!el) return;
      modo = "texto";
      // "plaintext-only" evita que se pegue formato; si el navegador no lo soporta, se usa "true".
      try { el.contentEditable = "plaintext-only"; } catch { el.contentEditable = "true"; }
      el.focus();
      const caja = v.tipo === "portada" ? null : el.closest(".is-texto-hoja");
      if (caja) hacerMovible(caja);
      const panel = $("panel");
      panel.innerHTML = `<span class="is-nota">${caja ? "Escribe directo en la hoja. Arrastra la barra del cuadro para moverlo y la esquina para cambiar su ancho." : "Escribe directo en la hoja."}</span>
        <div class="is-fila-compacta">${caja ? '<button type="button" class="is-boton-sec is-chico" data-p="menos" aria-label="Letra más chica">A−</button><button type="button" class="is-boton-sec is-chico" data-p="mas" aria-label="Letra más grande">A+</button>' : ""}<button type="button" class="is-boton-sec is-chico" data-p="cancelar">Cancelar</button><button type="button" class="is-boton is-chico" data-p="guardar">Guardar</button></div>`;
      panel.classList.remove("is-oculto");
      panel.querySelectorAll('[data-p="menos"], [data-p="mas"]').forEach((b) => b.addEventListener("click", () => {
        const f = Number(caja.style.getPropertyValue("--is-escala")) || 1;
        caja.style.setProperty("--is-escala", Math.min(1.6, Math.max(0.7, f + (b.dataset.p === "mas" ? 0.1 : -0.1))).toFixed(1));
      }));
      panel.querySelector('[data-p="cancelar"]').addEventListener("click", cerrarPanel);
      panel.querySelector('[data-p="guardar"]').addEventListener("click", async (ev) => {
        ev.target.disabled = true;
        try {
          // En celular el texto va bajo la lámina, así que ahí no se guarda posición.
          const libre = caja && getComputedStyle(caja).position === "absolute";
          await llamar(`/paginas/${v.n}`, "PUT", { texto: el.innerText.trim(), caja: libre ? medirCaja(caja) : undefined });
          cerrarPanel();
          await consultar();
        } catch (e) {
          mostrarError(e.message);
          ev.target.disabled = false;
        }
      });
    });

    $("redibujar").addEventListener("click", () => {
      if (modo) return cerrarPanel();
      const v = vistas[actual];
      const { usadas, max } = cuento.regeneraciones;
      modo = "dibujo";
      const panel = $("panel");
      panel.innerHTML = `<textarea class="is-campo" rows="2" maxlength="400" placeholder="Ej.: que sea de noche y que Toby tenga un pañuelo rojo"></textarea>
        <div class="is-fila-compacta is-panel-pie"><span class="is-nota">Cambios de ilustración: ${usadas} de ${max}</span>
        <button type="button" class="is-boton-sec is-chico" data-p="cancelar">Cancelar</button>
        <button type="button" class="is-boton is-chico" data-p="dibujar" ${usadas >= max ? "disabled" : ""}>Dibujar de nuevo</button></div>`;
      panel.classList.remove("is-oculto");
      panel.querySelector("textarea").focus();
      panel.querySelector('[data-p="cancelar"]').addEventListener("click", cerrarPanel);
      panel.querySelector('[data-p="dibujar"]').addEventListener("click", async (ev) => {
        ev.target.disabled = true;
        try {
          await llamar(`/paginas/${v.n}/regenerar`, "POST", { indicacion: panel.querySelector("textarea").value });
          cerrarPanel();
          await consultar();
        } catch (e) {
          mostrarError(e.message);
          ev.target.disabled = false;
        }
      });
    });

    // --- Pago: agrega el libro al carro con el id del cuento y va directo al checkout ---

    async function comprar(ev) {
      const variante = raiz.dataset.variante;
      if (!variante) return mostrarError("Falta configurar el producto del libro.");
      const boton = ev.currentTarget;
      const texto = boton.textContent;
      boton.disabled = true;
      boton.textContent = "Preparando tu compra…";
      try {
        const enlace = `${location.origin}${location.pathname}?id=${encodeURIComponent(id)}`;
        const r = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "content-type": "application/json", accept: "application/json" },
          body: JSON.stringify({ items: [{ id: Number(variante), quantity: 1, properties: { _cuento: id, Cuento: cuento.titulo || "Mi cuento", "Ver tu cuento": enlace } }] })
        });
        if (!r.ok) throw new Error();
        location.href = "/checkout";
      } catch {
        mostrarError("No pudimos agregar el libro al carro. Inténtalo de nuevo.");
        boton.disabled = false;
        boton.textContent = texto;
      }
    }

    $("pdf").addEventListener("click", async (ev) => {
      const boton = ev.currentTarget;
      boton.disabled = true;
      const original = boton.textContent;
      boton.textContent = "Armando…";
      try {
        await descargarPdf(cuento);
      } catch {
        mostrarError("No pudimos armar el PDF. Inténtalo de nuevo en un momento.");
      }
      boton.disabled = false;
      boton.textContent = original;
    });

    consultar();
  }

  // --- PDF: portada de 21 x 21 cm y dobles páginas de 42 x 21 cm con el texto donde lo dejó la familia ---
  async function descargarPdf(cuento) {
    if (!window.jspdf) await cargarScript(JSPDF);
    const { jsPDF } = window.jspdf;
    const paginas = cuento.paginas.filter((p) => !p.bloqueada && p.imagen).sort((a, b) => a.n - b.n);
    const doc = new jsPDF({ unit: "mm", format: [210, 210], orientation: "portrait" });
    for (const [i, p] of paginas.entries()) {
      const portada = p.n === 0;
      const [ancho, alto] = portada ? [210, 210] : [420, 210];
      if (i > 0) doc.addPage([ancho, alto], portada ? "portrait" : "landscape");
      const img = await comoJpeg(p.imagen);
      // Cubre la hoja recortando lo que sobre, como "object-fit: cover".
      const escala = Math.max(ancho / img.ancho, alto / img.alto);
      const w = img.ancho * escala;
      const h = img.alto * escala;
      doc.addImage(img.datos, "JPEG", (ancho - w) / 2, (alto - h) / 2, w, h);
      const texto = portada ? cuento.titulo || "" : p.texto || "";
      if (!texto) continue;
      doc.setFont("times", portada ? "bold" : "normal");
      const caja = portada ? null : p.caja;
      doc.setFontSize(portada ? 30 : 18 * (caja?.f || 1));
      // Portada: título arriba. Páginas: la caja guardada o, si no hay, centrada en el lado despejado.
      const anchoCaja = portada ? 186 : caja ? (caja.w / 100) * ancho : 150;
      const lineas = doc.splitTextToSize(texto, anchoCaja - 20);
      const altoLinea = portada ? 12 : 8.2 * (caja?.f || 1);
      const altoCaja = lineas.length * altoLinea + 14;
      const x = portada ? 12 : caja ? (caja.x / 100) * ancho : p.lado === "izquierda" ? 16 : ancho - 16 - anchoCaja;
      const y = portada ? 14 : caja ? Math.min((caja.y / 100) * alto, alto - altoCaja) : (alto - altoCaja) / 2;
      doc.setFillColor(255, 255, 255);
      doc.setGState(new doc.GState({ opacity: 0.82 }));
      doc.roundedRect(x, y, anchoCaja, altoCaja, 6, 6, "F");
      doc.setGState(new doc.GState({ opacity: 1 }));
      doc.setTextColor(19, 32, 74);
      if (portada) doc.text(lineas, ancho / 2, y + 7 + altoLinea * 0.75, { align: "center" });
      else doc.text(lineas, x + 10, y + 7 + altoLinea * 0.75);
    }
    const nombre = (cuento.titulo || "mi-cuento").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^\w]+/g, "-").replace(/^-|-$/g, "").toLowerCase();
    doc.save(`${nombre || "mi-cuento"}.pdf`);
  }

  async function comoJpeg(url) {
    const r = await fetch(url, { mode: "cors", cache: "no-store" });
    if (!r.ok) throw new Error("imagen");
    const img = new Image();
    img.src = URL.createObjectURL(await r.blob());
    await img.decode();
    const lienzo = document.createElement("canvas");
    lienzo.width = img.naturalWidth;
    lienzo.height = img.naturalHeight;
    const ctx = lienzo.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, lienzo.width, lienzo.height);
    ctx.drawImage(img, 0, 0);
    URL.revokeObjectURL(img.src);
    return { datos: lienzo.toDataURL("image/jpeg", 0.92), ancho: lienzo.width, alto: lienzo.height };
  }

  function cargarScript(src) {
    return new Promise((ok, mal) => {
      const s = document.createElement("script");
      s.src = src;
      s.onload = ok;
      s.onerror = mal;
      document.head.appendChild(s);
    });
  }

  const todos = () => document.querySelectorAll('[id^="is-cuento-"]').forEach(iniciar);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", todos);
  else todos();
  document.addEventListener("shopify:section:load", todos);
})();
